import { Card, CardContent, Box, Typography } from "@mui/material";
import { BarChart, Bar, ResponsiveContainer, LineChart, Line } from "recharts";
import BuildIcon from "@mui/icons-material/Build";
import { Wrench, TriangleAlert, Clock, Activity } from "lucide-react";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Skeleton } from "@mui/material";

// ─── Config ──────────────────────────────────────────────────────────────────

const CARD_META = {
  workOrders: {
    icon: Wrench,
    sparkColor: "#6366F1",
    lowerIsBetter: true,
    isLineChart: false,
    isLucide: true,
  },
  assetsAtRisk: {
    icon: TriangleAlert,
    sparkColor: "#EF4444",
    lowerIsBetter: true,
    isLineChart: false,
    isLucide: true,
  },
  averageMttr: {
    icon: Clock,
    sparkColor: "#F59E0B",
    lowerIsBetter: true,
    isLineChart: false,
    isLucide: true,
  },
  oee: {
    icon: TrendingUpIcon,
    sparkColor: "#22C55E",
    lowerIsBetter: false,
    isLineChart: true,
  },
  serviceCosts: {
    icon: Activity,
    sparkColor: "#8B5CF6",
    lowerIsBetter: true,
    isLineChart: false,
    isLucide: true,
  },
  averageRul: {
    icon: Clock,
    sparkColor: "#3B82F6",
    lowerIsBetter: false,
    isLineChart: false,
    isLucide: true,
  },
  outstandingWorkOrders: {
    icon: Wrench,
    sparkColor: "#6366F1",
    lowerIsBetter: true,
    isLineChart: false,
    isLucide: true,
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatValue = (id, raw) => {
  switch (id) {
    case "serviceCosts":
      return `$${parseFloat(raw || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
      })}`;
    case "averageMttr":
      const mttrVal = parseFloat(raw);
      return isNaN(mttrVal) ? "0h" : `${mttrVal.toFixed(0)}h`;  // ← fix here
    case "oee":
      return String(raw).includes("%") ? raw : `${raw}%`;
    case "averageRul":
      return Number(raw).toLocaleString();
    default:
      return String(raw ?? 0);
  }
};

const extractSparkline = (sparklineData) => {
  if (!sparklineData || sparklineData.length === 0) return null;
  return sparklineData.map((entry) => ({
    value:
      entry.dailyCount !== undefined
        ? entry.dailyCount
        : entry.dailyCost !== undefined
          ? entry.dailyCost
          : entry.dailyAvgMttr !== undefined
            ? entry.dailyAvgMttr
            : 0,
  }));
};

const resolveChangeType = (id, percentageChange) => {
  if (!percentageChange || percentageChange === "N/A" || percentageChange === "%")
    return null;
  const isDecrease = parseFloat(percentageChange) < 0;
  const lowerIsBetter = CARD_META[id]?.lowerIsBetter ?? false;
  return (lowerIsBetter ? isDecrease : !isDecrease) ? "positive" : "negative";
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * KpiCard
 *
 * Props:
 *   id   — key matching the API response (e.g. "workOrders", "oee")
 *   data — raw object from api response e.g. apiData.workOrders
 *
 * Usage:
 *   <KpiCard id="workOrders" data={apiData.workOrders} />
 */
const KpiCard = ({ id, data, loading }) => {
  if (loading) {
    return (
      <Box
        sx={{
          p: 2.5,
          borderRadius: 1,
          border: "1px solid #E5E7EB",
          bgcolor: "#fff",
          height: "100%",
        }}
      >
        <Skeleton
          variant="text"
          width="50%"
          height={20}
          sx={{ mb: 1, borderRadius: 1 }}
        />
        <Skeleton
          variant="text"
          width="35%"
          height={40}
          sx={{ mb: 1, borderRadius: 1 }}
        />
        <Skeleton
          variant="text"
          width="65%"
          height={16}
          sx={{ borderRadius: 1 }}
        />
      </Box>
    );
  }

  if (!data) return null;

  const meta = CARD_META[id] || {
    icon: BuildIcon,
    sparkColor: "#6366F1",
    isLineChart: false,
  };
  const IconComponent = meta.icon;

  const rawValue = data.value ?? data.outstandingWorkOrders;
  const title = data.cardTitle;
  const value = formatValue(id, rawValue);
  const change =
  data.percentageChange &&
  data.percentageChange !== "N/A" &&
  data.percentageChange !== "%"   
    ? data.percentageChange
    : null;
  const changeType = resolveChangeType(id, data.percentageChange);
  const subtitle = data.comparisonPeriod;
  const chartData = extractSparkline(data.sparklineData);

  return (
    <Card
      elevation={0}
      sx={{ height: "100%", boxShadow: "none", border: "1px solid #E5E7EB" }}
    >
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: "24px",
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "24px",
              color: "#020618",
            }}
          >
            {title}
          </Typography>
          {meta.isLucide ? (
            <IconComponent size={16} color="#555555" strokeWidth={2} />
          ) : (
            <IconComponent sx={{ fontSize: 18, color: "#9CA3AF" }} />
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  fontSize: "24px",
                  lineHeight: "32px",
                  color: "#0A0A0A",
                }}
              >
                {value}
              </Typography>
              {id === "outstandingWorkOrders" && data.total && data.total !== "0" && (
                <Typography sx={{ fontWeight: 400, fontSize: "14px", color: "#555555" }}>
                  out of {data.total}
                </Typography>
              )}
              {change && (
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "20px",
                    color: changeType === "positive" ? "#22C55E" : "#EF4444",
                  }}
                >
                  {change}
                </Typography>
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "20px",
                color: "#555555",
              }}
            >
              {subtitle?.toLowerCase()}
            </Typography>
          </Box>
          {chartData && (
            <Box sx={{ width: 80, height: 40 }}>
              <ResponsiveContainer width="100%" height="100%">
                {meta.isLineChart ? (
                  <LineChart data={chartData}>
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={meta.sparkColor}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                ) : (
                  <BarChart data={chartData}>
                    <Bar
                      dataKey="value"
                      fill={meta.sparkColor}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default KpiCard;
